import React from 'react'
import {
  EnergyFlow,
  EnergyType,
  isPrimaryEnergyFlow,
  isPrimaryEnergyFlowEqual,
  isSecondaryEnergyFlow,
  PrimaryEnergyFlow,
  Quadrant,
  SecondaryEnergyFlow,
} from '../../../calculators/EnergyFlow'
import { reduceToSumOfItems, sum } from '../../../util/array-utility'
import {
  formatEnergy,
  formatPercent,
  formatPower,
} from '../../../util/formatters'
import { Provider } from '../../../calculators/providers/common/Provider'
import { SystemContainerCalculator } from '../../../calculators/SystemContainerCalculator'
import { SpecialDemand } from '../../../calculators/special-demand-ids'

export enum ConsumptionType {
  TotalEnergy = 'energy',
  HourlyPower = 'power',
}

export type UsageMatrixUtils = {
  formatTotalEnergyOrPowerByAllProviders: () => React.ReactNode

  formatTotalEnergyOrPowerForProvider: (providerId: string) => React.ReactNode

  formatTotalEnergyOrPowerForDemand: (demandId: string) => React.ReactNode

  formatProvidedEnergyOrPower: (
    providerId: string,
    demandId: string,
    isConnected: boolean,
  ) => React.ReactNode
}

export function createUsageMatrixUtils(
  providersById: Record<string, Provider>,
  calculator: SystemContainerCalculator,
  energyType: EnergyType,
  consumptionType: ConsumptionType,
  hourOfYear: number,
): UsageMatrixUtils {
  const isProvidedAndCorrectEnergyType = (
    flow: EnergyFlow,
  ): flow is PrimaryEnergyFlow =>
    isPrimaryEnergyFlow(flow) &&
    flow.quadrant === Quadrant.Provided &&
    flow.energyType === energyType

  const isSecondaryAndProvidedAndCorrectEnergyType = (
    flow: EnergyFlow,
  ): flow is SecondaryEnergyFlow =>
    isSecondaryEnergyFlow(flow) &&
    flow.quadrant === Quadrant.Provided &&
    flow.energyType === energyType

  const getProvidedFlowsForProvider = (provider: Provider): EnergyFlow[] => {
    const providedPrimaryEnergyFlows = (provider.flows ?? []).filter(
      isProvidedAndCorrectEnergyType,
    )
    const providedSecondaryEnergyFlows =
      provider.providesElectricityForProviders
        ? (provider.flows ?? []).filter(
            isSecondaryAndProvidedAndCorrectEnergyType,
          )
        : []

    return [...providedPrimaryEnergyFlows, ...providedSecondaryEnergyFlows]
  }

  const getProvidedFlowsForDemand = (
    flows: EnergyFlow[],
    demandId: string,
  ): EnergyFlow[] =>
    demandId === SpecialDemand.OtherProvidersElectricity
      ? flows.filter(isSecondaryAndProvidedAndCorrectEnergyType)
      : flows
          .filter(isProvidedAndCorrectEnergyType)
          .filter(isPrimaryEnergyFlowEqual(demandId, energyType))

  const totalProvidedEnergyByAllProviders = sum(
    Object.values(providersById)
      .flatMap(getProvidedFlowsForProvider)
      .flatMap((flow) => flow.hourSeries),
  )

  const totalProvidedPowerByAllProviders = sum(
    Object.values(providersById)
      .flatMap((provider) => provider.flows ?? [])
      .filter(isProvidedAndCorrectEnergyType)
      .map((flow) => flow.hourSeries[hourOfYear]),
  )

  const formatTotalEnergyOrPowerByAllProviders = () => {
    if (consumptionType === ConsumptionType.TotalEnergy) {
      return formatEnergy(totalProvidedEnergyByAllProviders)
    }

    return formatPower(totalProvidedPowerByAllProviders)
  }

  const formatTotalEnergyOrPowerForProvider = (providerId: string) => {
    const calculatorProvider = providersById[providerId]
    if (!calculatorProvider) {
      return '-'
    }

    const providedEnergyFlows = getProvidedFlowsForProvider(calculatorProvider)

    if (consumptionType === ConsumptionType.TotalEnergy) {
      const totalProvidedEnergy = sum(
        providedEnergyFlows.flatMap((flow) => flow.hourSeries),
      )

      const percentOfTotal =
        totalProvidedEnergy / totalProvidedEnergyByAllProviders

      return (
        <>
          {formatPercent(percentOfTotal)}
          <br />
          {formatEnergy(totalProvidedEnergy)}
        </>
      )
    }

    const totalProvidedPower = sum(
      providedEnergyFlows.map((flow) => flow.hourSeries[hourOfYear], 0),
    )

    return formatPower(totalProvidedPower)
  }

  const formatTotalEnergyOrPowerForDemand = (demandId: string) => {
    const flows = calculator.getProviderEnergyFlows()
    const providedEnergyFlows: EnergyFlow[] = getProvidedFlowsForDemand(
      flows,
      demandId,
    )

    if (consumptionType === ConsumptionType.TotalEnergy) {
      const totalProvidedEnergy = sum(
        providedEnergyFlows.flatMap((flow) => flow.hourSeries),
      )

      return formatEnergy(totalProvidedEnergy)
    }

    const totalProvidedPower = sum(
      providedEnergyFlows.map((flow) => flow.hourSeries[hourOfYear], 0),
    )

    return formatPower(totalProvidedPower)
  }

  const formatProvidedEnergyOrPower = (
    providerId: string,
    demandId: string,
    isConnected: boolean,
  ) => {
    const calculatorProvider = providersById[providerId]
    if (!calculatorProvider) {
      return '-'
    }

    if (!isConnected) {
      return '-'
    }

    const providedFlows = getProvidedFlowsForDemand(
      calculatorProvider.flows ?? [],
      demandId,
    )
    const providedEnergyFlow = {
      hourSeries: providedFlows
        .map((flow) => flow.hourSeries)
        .reduce(reduceToSumOfItems, []),
    }

    if (consumptionType === ConsumptionType.TotalEnergy) {
      return formatEnergy(
        providedEnergyFlow ? sum(providedEnergyFlow.hourSeries) : 0,
      )
    }

    return formatPower(providedEnergyFlow?.hourSeries[hourOfYear] ?? 0)
  }

  return {
    formatTotalEnergyOrPowerByAllProviders,
    formatTotalEnergyOrPowerForProvider,
    formatTotalEnergyOrPowerForDemand,
    formatProvidedEnergyOrPower,
  }
}
